import React, { createContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsProvider as IAnalyticsProvider, AnalyticsConfig } from './types';
import { useUserStore } from '../store/userStore';

interface AnalyticsContextValue {
  provider: IAnalyticsProvider;
}

export const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

function detectEnvironment(): 'development' | 'staging' | 'production' {
  const override = process.env.REACT_APP_ENVIRONMENT;
  if (override === 'development' || override === 'staging' || override === 'production') {
    return override;
  }
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  if (hostname.includes('stage') || hostname.includes('dev')) return 'staging';
  return 'production';
}

function PageTracker({ provider }: { provider: IAnalyticsProvider }) {
  const location = useLocation();

  useEffect(() => {
    provider.pageView(location.pathname);
  }, [location.pathname, provider]);

  return null;
}

function AnalyticsIdentifier({ provider }: { provider: IAnalyticsProvider }) {
  const user = useUserStore((s) => s.user);
  const prevUserId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (user && user._id !== prevUserId.current) {
      provider.identify({
        id: user._id,
        email: user.email,
        name: user.name,
        isAdmin: user.admin,
        position: user.position,
        profession: user.profession?.name,
        tenantId: user.tenantId,
      });
      prevUserId.current = user._id;
    } else if (!user && prevUserId.current) {
      provider.reset();
      prevUserId.current = undefined;
    }
  }, [user, provider]);

  return null;
}

interface AnalyticsProviderProps {
  provider: IAnalyticsProvider;
  config: Omit<AnalyticsConfig, 'environment'> & { environment?: AnalyticsConfig['environment'] };
  children: React.ReactNode;
}

export function AnalyticsProviderComponent({ provider, config, children }: AnalyticsProviderProps) {
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    provider.init({
      ...config,
      environment: config.environment ?? detectEnvironment(),
    });
    isInitialized.current = true;
  }, [provider, config]);

  return (
    <AnalyticsContext.Provider value={{ provider }}>
      <PageTracker provider={provider} />
      <AnalyticsIdentifier provider={provider} />
      {children}
    </AnalyticsContext.Provider>
  );
}
